import { useMemo } from 'react';
import { SearchMembersQuery } from '@frontend/app/queries/types/SearchMembersQuery';
import { GetMembersForIdsQuery } from '@frontend/app/queries/types/GetMembersForIdsQuery';
import { ApolloError } from '@apollo/client';

/**
 * Hook for combining and deduplicating members data from search and memberIds queries
 * @param searchData Data from search query
 * @param searchLoading Loading state from search query
 * @param searchError Error from search query
 * @param memberIdsData Data from memberIds query
 * @param memberIdsLoading Loading state from memberIds query
 * @param memberIdsError Error from memberIds query
 * @returns Combined and deduplicated members data with loading and error states
 */
export const useMergeMembersData = (
  searchData?: SearchMembersQuery | null,
  searchLoading?: boolean,
  searchError?: ApolloError | undefined,
  memberIdsData?: GetMembersForIdsQuery | null,
  memberIdsLoading?: boolean,
  memberIdsError?: ApolloError | undefined,
) => {
  // Memoize the combined and deduplicated members to prevent unnecessary calculations
  const uniqueMembers = useMemo(() => {
    // Combine the results
    const combinedMembers = [
      ...(searchData?.members || []),
      ...(memberIdsData?.members || [])
    ];

    // Use a Map for O(1) lookups instead of O(n) find() operations
    const memberMap = new Map();

    // Populate the map - later entries with same ID will overwrite earlier ones
    combinedMembers.forEach((member) => {
      memberMap.set(member.id, member);
    });

    // Convert map values back to array
    return Array.from(memberMap.values()) as SearchMembersQuery['members'];
  }, [searchData?.members, memberIdsData?.members]);

  // Memoize the final result object
  const result = useMemo(() => ({
    data: { members: uniqueMembers },
    loading: searchLoading || memberIdsLoading,
    error: searchError || memberIdsError
  }), [uniqueMembers, searchLoading, memberIdsLoading, searchError, memberIdsError]);

  return result;
};
